import dayjs from "dayjs";

export const formatDate = (date: string | number, format: string = "YYYY.MM.DD HH:mm:ss") => {
  return dayjs(date).format(format);
};

/**
 * 버전 유효 기간(validFrom/validTo) 표시용 포매터.
 * "YYYY-MM-DD" 또는 "YYYY-MM-DDTHH:mm:ssZ" 등 ISO 문자열에서 시간/타임존을 strip하고 "YYYY.MM.DD" 형태로 반환.
 * 시간을 표시하지 않으므로 타임존 노출/혼동 이슈가 발생하지 않음.
 */
export const formatValidDate = (date: string): string => {
  return date.slice(0, 10).replaceAll("-", ".");
};

// kenny: 아래는 mash-design-system formatter 함수
type SystemFormatTimeDurationOptions = {
  abbreviate?: boolean;
  compactly?: boolean;
  showZero?: boolean;
};

type SystemTimeDurationDistributionFormatOption = {
  interval?: number;
};

type SystemFormatCalendarYearOption = {
  format?: string;
};

type SystemDateSeriesFormatOption = {
  format?: string;
  start?: string | number;
  unit?: dayjs.UnitTypeShort;
};

const ONE_SEC = 1;
const TEN_SEC = 10;
const SEC_FOR_MIN = 60;
const SEC_FOR_HOUR = 60 * SEC_FOR_MIN;

const DURATION_UNITS: Record<string, [string, string, string]> = {
  en: ["h", "m", "s"],
  ko: ["시간", "분", "초"],
};

const DURATION_COMPACT_UNITS: Record<string, [string, string, string]> = {
  en: ["h", "m", "s"],
  ko: ["시간", "분", "초"],
};

const DEFAULT_NUMBER_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  maximumFractionDigits: 2,
};

const DEFAULT_FORMAT_TIME_DURATION_OPTIONS: Required<SystemFormatTimeDurationOptions> = {
  abbreviate: false,
  compactly: false,
  showZero: false,
};

const DEFAULT_TIME_DURATION_DISTRIBUTION_FORMAT_OPTION: Required<SystemTimeDurationDistributionFormatOption> =
  {
    interval: 5,
  };

const DEFAULT_DATE_SERIES_FORMAT_OPTION: Required<SystemDateSeriesFormatOption> = {
  format: "YYYY.MM.DD",
  start: dayjs().valueOf(),
  unit: "d",
};

const CALENDAR_YEAR_MONTH_FORMAT: Record<string, string> = {
  en: "YYYY MMM",
  ko: "YYYY년 MM월",
};

const abbreviateTimeValue = (value: number) => {
  let abbreviatedValue = value;
  if (value || 0 < ONE_SEC) {
    // 1초 미만
    abbreviatedValue = Number(Math.floor(value * 10) / 10); // 0.n초
  } else if ((value || 0) < TEN_SEC) {
    // 10초 미만
    abbreviatedValue = Math.floor(value); // n초
  } else if ((value || 0) < 1 * SEC_FOR_MIN) {
    // 1분 미만
    abbreviatedValue = Math.round(value / 10) * 10; // 10초 반올림
  } else if ((value || 0) < 4 * SEC_FOR_MIN) {
    // 4분 미만
    abbreviatedValue = Math.round(value / 10) * 10; // 10초 반올림
  } else if (value < 40 * SEC_FOR_MIN) {
    // 40분 미만
    abbreviatedValue = Math.round(value / 60) * 60; // 1분 반올림
  } else if (value >= SEC_FOR_HOUR * 10) {
    // 10 시간 이상
    abbreviatedValue = Math.floor(value / SEC_FOR_HOUR) * SEC_FOR_HOUR; // 1시간 내림
  } else {
    abbreviatedValue = Math.round(value / 600) * 600; // 10분 반올림
  }
  return abbreviatedValue;
};

const separateSecondIntoTime = (value: number, abbreviate: boolean): [number, number, number] => {
  value = Math.floor(value); // 소수점이하 제거
  let remains = abbreviate ? abbreviateTimeValue(value) : value;
  let hour = NaN;
  let minute = NaN;

  if (remains >= SEC_FOR_HOUR) {
    hour = (remains - (remains % SEC_FOR_HOUR)) / SEC_FOR_HOUR;
    remains = remains % SEC_FOR_HOUR;
  }

  if (remains >= SEC_FOR_MIN) {
    minute = (remains - (remains % SEC_FOR_MIN)) / SEC_FOR_MIN;
    remains = remains % SEC_FOR_MIN;
  }

  return [hour, minute, remains];
};

export const systemFormatDate = (
  value: number | string,
  lng?: string,
  options: { format: string } = { format: "YYYY.MM.DD" }
) => {
  return dayjs(value)
    .locale(lng ?? "en")
    .format(options.format);
};

export const systemFormatNumber = (
  value: number | bigint,
  lng?: string,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat(lng, {
    ...options,
    maximumFractionDigits:
      options.maximumFractionDigits ?? DEFAULT_NUMBER_FORMAT_OPTIONS.maximumFractionDigits,
  }).format(value);
};

export const systemFormatNumberCompact = (
  value: number | bigint,
  lng?: string,
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat(lng, {
    notation: "compact",
    compactDisplay: "short",
    ...options,
    maximumFractionDigits:
      options.maximumFractionDigits ?? DEFAULT_NUMBER_FORMAT_OPTIONS.maximumFractionDigits,
  }).format(value);
};

export const systemFormatTimeDuration = (
  value: number,
  lng = "en",
  options: SystemFormatTimeDurationOptions = {}
) => {
  const { abbreviate, showZero, compactly } = {
    ...DEFAULT_FORMAT_TIME_DURATION_OPTIONS,
    ...options,
  };
  const unitSet = compactly ? DURATION_COMPACT_UNITS : DURATION_UNITS;
  const units = unitSet[lng] ?? unitSet.en;
  if (value < 60) {
    if (0 < value && value < 1) {
      value = +systemFormatNumber(value, lng, { maximumFractionDigits: 2 });
    } else {
      value = +systemFormatNumber(value, lng, { maximumFractionDigits: 0 });
    }
    return [value, units.at(-1)].join("");
  } else {
    const showTwoUnitsOnly = compactly || abbreviate;
    const realShowZero = !abbreviate && showZero;
    return separateSecondIntoTime(value, abbreviate)
      .reduce<string[]>((result, current, index) => {
        if (Number.isNaN(current)) return result;
        if (current === 0 && !realShowZero) return result; // 0값 가리기
        if (showTwoUnitsOnly && result.length >= 2) return result; // 두 자리 수 이상 조건에서 루프 탈출
        const unit = units[index];
        result.push(`${current}${unit}`);
        return result;
      }, [])
      .filter((part) => typeof part === "string")
      .join(" ");
  }
};

export const systemFormatDateSeries = (
  value: number,
  lng = "en",
  options: SystemDateSeriesFormatOption = {}
) => {
  const _options = { ...DEFAULT_DATE_SERIES_FORMAT_OPTION, ...options };
  return dayjs(_options.start).locale(lng).add(+value, _options.unit).format(_options.format);
};

export const systemFormatTimeDurationDistribution = (
  value: number | string,
  lng = "en",
  options: SystemTimeDurationDistributionFormatOption = {}
) => {
  if (typeof value === "string") value = parseInt(value);
  const { interval } = { ...DEFAULT_TIME_DURATION_DISTRIBUTION_FORMAT_OPTION, ...options };
  const [, min, sec] = DURATION_UNITS[lng] ?? DURATION_UNITS.en;
  const unit = interval < 60 ? sec : min;
  const tick = interval >= 60 ? interval / 60 : interval;
  return `${tick * value}-${tick * (value + 1)}${unit}`;
};

export const systemFormatCalendarYearMonth = (value: number, lng = "en") => {
  return dayjs(value).locale(lng).format(CALENDAR_YEAR_MONTH_FORMAT[lng]);
};

export const systemFormatCalendarYear = (
  value: number,
  lng = "en",
  { format = "YYYY" }: SystemFormatCalendarYearOption
) => {
  return dayjs(`${value}-01-01`).locale(lng).format(format);
};
